import React, { useState, useEffect } from "react";
import { Bell, X, ShieldCheck } from "lucide-react";
import { registerDeviceFCM, onForegroundMessage } from "../../services/fcm/fcmService";
import { auth } from "../../utils/firebase";

const DISMISSED_KEY = "fcm_push_activation_dismissed_v1";

export const PushNotificationActivationBanner: React.FC = () => {
  const [showBanner, setShowBanner] = useState(false);
  const [loading, setLoading] = useState(false);
  const [statusMessage, setStatusMessage] = useState<string | null>(null);

  useEffect(() => {
    // 1. Verifica suporte no navegador
    if (typeof window === "undefined" || typeof Notification === "undefined") {
      return;
    }

    // 2. Se já estiver 'granted', registra/atualiza o dispositivo silenciosamente
    if (Notification.permission === "granted") {
      const currentUser = auth?.currentUser;
      if (currentUser?.uid) {
        registerDeviceFCM({ forceRequestPermission: false }).catch(() => {});
      }
      return;
    }

    // 3. Se permission === 'denied', respeita a escolha do usuário e não exibe
    if (Notification.permission === "denied") {
      return;
    }

    // 4. Se permission === 'default', verifica se o usuário já dispensou manualmente o banner
    try {
      const isDismissed = localStorage.getItem(DISMISSED_KEY);
      if (isDismissed === "true") {
        return;
      }
    } catch (e) {}

    // Exibe banner discreto após breves instantes se houver usuário autenticado
    const timer = setTimeout(() => {
      const currentUser = auth?.currentUser;
      if (currentUser?.uid) {
        setShowBanner(true);
      }
    }, 1500);

    return () => clearTimeout(timer);
  }, []);

  // Escuta mensagens FCM em primeiro plano (foreground)
  useEffect(() => {
    let unsubscribe: (() => void) | null = null;
    onForegroundMessage((payload) => {
      console.log("[PushNotificationActivationBanner] Notificação em primeiro plano recebida:", payload);
    }).then((unsub) => {
      if (unsub) unsubscribe = unsub;
    });

    return () => {
      if (unsubscribe) unsubscribe();
    };
  }, []);

  const handleActivate = async () => {
    setLoading(true);
    setStatusMessage(null);
    try {
      const result = await registerDeviceFCM({ forceRequestPermission: true });
      if (result.success) {
        setStatusMessage("Notificações ativadas!");
        setTimeout(() => {
          setShowBanner(false);
        }, 2000);
      } else if (result.reason === "PERMISSION_DENIED") {
        setShowBanner(false);
      } else {
        setStatusMessage(result.message || "Não foi possível ativar.");
        setTimeout(() => setStatusMessage(null), 3000);
      }
    } catch (err) {
      console.error("[PushNotificationActivationBanner] Erro ao ativar notificações:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleDismiss = () => {
    setShowBanner(false);
    try {
      localStorage.setItem(DISMISSED_KEY, "true");
    } catch (e) {}
  };

  if (!showBanner) return null;

  return (
    <div className="bg-emerald-700 dark:bg-emerald-900 text-white px-4 py-2.5 shadow-lg border-b border-emerald-600/50 flex flex-wrap items-center justify-between gap-3 text-xs sm:text-sm relative z-30 transition-all">
      <div className="flex items-center gap-2.5">
        <div className="p-1.5 bg-emerald-800/80 rounded-lg shrink-0">
          <Bell size={16} className="text-amber-300 animate-pulse" />
        </div>
        <p className="font-medium text-emerald-50 leading-tight">
          Ative as notificações para receber avisos de agendamentos, pagamentos e horários.
        </p>
      </div>
      <div className="flex items-center gap-2 shrink-0">
        {statusMessage ? (
          <span className="text-amber-300 font-bold flex items-center gap-1">
            <ShieldCheck size={14} /> {statusMessage}
          </span>
        ) : (
          <>
            <button
              type="button"
              onClick={handleActivate}
              disabled={loading}
              className="px-3 py-1 bg-amber-400 hover:bg-amber-300 text-emerald-950 font-black rounded-lg transition text-xs cursor-pointer disabled:opacity-50"
            >
              {loading ? "ATIVANDO..." : "ATIVAR NOTIFICAÇÕES"}
            </button>
            <button
              type="button"
              onClick={handleDismiss}
              className="p-1 hover:bg-emerald-800 rounded-lg transition text-emerald-200 hover:text-white cursor-pointer"
              aria-label="Agora não"
              title="Agora não"
            >
              <X size={16} />
            </button>
          </>
        )}
      </div>
    </div>
  );
};
